/**
 * Promo record shape stored in Firestore /promos/{id}.
 *
 * Fields:
 *   kind            'code' | 'date'
 *                   'code' promos activate when their `code` is present in
 *                   the URL (?promo=) or entered manually. They may also
 *                   carry an optional start/end window.
 *                   'date' promos activate for every visitor whenever
 *                   now is within [startsAt, endsAt]. No code required.
 *   name            Internal label shown in the admin (e.g. "Emma collab
 *                   Q2 2026", "Black Friday 2026"). Never shown to buyers.
 *   code            string, required for kind=='code'. Case-insensitive,
 *                   stored uppercased. Becomes the `tc=` Zazzle attribution
 *                   value lowercased (e.g. EMMA -> tc=emma).
 *   discountPercent number, must be in SUPPORTED_DISCOUNT_PERCENTS.
 *   startsAt        Firestore Timestamp or null. Inclusive.
 *   endsAt          Firestore Timestamp or null. Inclusive.
 *   active          boolean. Master kill-switch independent of dates.
 *                   Inactive promos are ignored even within their window.
 *
 * Higher discount wins when multiple promos match (one date + one code).
 * Attribution: when a code-type promo is the winner, its code becomes the
 * Zazzle `tc` value. When only a date promo wins, `tc` stays at the
 * default 'geoshapedmug'.
 */

export const PROMO_KIND_CODE = 'code';
export const PROMO_KIND_DATE = 'date';

/**
 * Normalise a code for comparison: trim + uppercase.
 * Used both when reading from URL and when matching stored promos.
 */
export function normaliseCode(raw) {
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    return trimmed.toUpperCase();
}

/**
 * The Zazzle `tc=` value for a winning promo. Code promos use their code
 * lowercased; date promos keep the default so attribution doesn't get
 * polluted by sitewide sales.
 */
export const DEFAULT_TC = 'geoshapedmug';

export function tcForPromo(promo) {
    if (!promo) return DEFAULT_TC;
    if (promo.kind === PROMO_KIND_CODE && promo.code) {
        return promo.code.toLowerCase();
    }
    return DEFAULT_TC;
}
